import { useEffect, useState } from 'react';
import type { InviteRecipient, InviteRecipientStatus, RsvpResponseValue } from '@peles/shared';
import { EntityModal, SearchableListSelect } from '../common';
import { useDialog } from '../../context/DialogContext';
import { useUpdateCampaignRecipient } from '../../hooks/useInvites';

const RESPONSE_OPTIONS: { id: RsvpResponseValue; label: string }[] = [
  { id: 'PENDING', label: 'Pending — no answer yet' },
  { id: 'YES', label: 'Yes — attending' },
  { id: 'MAYBE', label: 'Maybe' },
  { id: 'NO', label: 'No — not attending' },
];

const STATUS_OPTIONS: { id: InviteRecipientStatus; label: string }[] = [
  { id: 'PENDING', label: 'Pending — not sent' },
  { id: 'QUEUED', label: 'Queued' },
  { id: 'SENT', label: 'Sent' },
  { id: 'DELIVERED', label: 'Delivered' },
  { id: 'OPENED', label: 'Opened' },
  { id: 'RESPONDED', label: 'Responded' },
  { id: 'BOUNCED', label: 'Bounced' },
  { id: 'FAILED', label: 'Failed' },
];

export interface RecipientEditModalProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  recipient: InviteRecipient | null;
}

/**
 * Producer override of one recipient's RSVP and delivery status.
 *
 * Guests answer by phone, in person, or to the wrong person — the campaign
 * list has to be correctable by hand. The response is written to the same
 * record the public page writes (marked as producer-entered) and mirrored
 * onto the guest row, so seating, exports and dashboards stay in agreement.
 */
export function RecipientEditModal({
  open,
  onClose,
  campaignId,
  recipient,
}: RecipientEditModalProps) {
  const { alert } = useDialog();
  const updateRecipient = useUpdateCampaignRecipient();

  const [response, setResponse] = useState<RsvpResponseValue>('PENDING');
  const [status, setStatus] = useState<InviteRecipientStatus>('PENDING');
  const [adultCount, setAdultCount] = useState('');
  const [kidCount, setKidCount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !recipient) return;
    setResponse(recipient.response ?? 'PENDING');
    setStatus(recipient.status);
    // Blank means "leave the party size as it is" — the counts live on the
    // guest and this modal is not the place to invent them.
    setAdultCount('');
    setKidCount('');
  }, [open, recipient]);

  const handleSave = async () => {
    if (!recipient) return;
    setSaving(true);
    try {
      await updateRecipient.mutateAsync({
        campaignId,
        recipientId: recipient.id,
        updates: {
          response,
          // Only send a status the producer actually touched. Resubmitting the
          // unchanged one would override the status the response implies —
          // clearing an answer back to Pending would leave the invitation
          // stuck on "Responded".
          ...(status !== recipient.status ? { status } : {}),
          ...(adultCount !== '' ? { adultCount: Number(adultCount) } : {}),
          ...(kidCount !== '' ? { kidCount: Number(kidCount) } : {}),
        },
      });
      onClose();
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Failed to update the recipient',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={recipient?.guestName ? `Edit RSVP — ${recipient.guestName}` : 'Edit RSVP'}
      size="sm"
      presentation="centered"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <span className="block text-sm font-medium text-gray-700">RSVP</span>
          <div className="mt-1">
            <SearchableListSelect
              items={RESPONSE_OPTIONS}
              value={response}
              onChange={(value) =>
                setResponse(typeof value === 'string' ? (value as RsvpResponseValue) : 'PENDING')
              }
              getValue={(option) => option.id}
              getLabel={(option) => option.label}
              ariaLabel="RSVP response"
              clearable={false}
            />
          </div>
        </div>

        {response !== 'NO' && response !== 'PENDING' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="recipient-adults" className="block text-sm font-medium text-gray-700">
                Adults
              </label>
              <input
                id="recipient-adults"
                type="number"
                min={0}
                max={100}
                value={adultCount}
                onChange={(e) => setAdultCount(e.target.value)}
                placeholder="Unchanged"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label htmlFor="recipient-kids" className="block text-sm font-medium text-gray-700">
                Children
              </label>
              <input
                id="recipient-kids"
                type="number"
                min={0}
                max={100}
                value={kidCount}
                onChange={(e) => setKidCount(e.target.value)}
                placeholder="Unchanged"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
        )}

        <div>
          <span className="block text-sm font-medium text-gray-700">Delivery status</span>
          <div className="mt-1">
            <SearchableListSelect
              items={STATUS_OPTIONS}
              value={status}
              onChange={(value) =>
                setStatus(typeof value === 'string' ? (value as InviteRecipientStatus) : 'PENDING')
              }
              getValue={(option) => option.id}
              getLabel={(option) => option.label}
              ariaLabel="Delivery status"
              clearable={false}
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Saving a response updates the guest&apos;s RSVP everywhere — seating, exports and the
          dashboard — and records it as entered by you.
        </p>
      </div>
    </EntityModal>
  );
}
